import { v4 as uuidv4 } from "uuid";

import ViewModel from "../ViewModel";
import AdminViewModel from "./AdminViewModel";
import User from "../../../domain/models/admin/User";
import AdminRepository from "../../../domain/repositories/admin/AdminRepository";
import FormValidator from "../../../util/FormValidator";

export default class AdminViewModelImpl extends ViewModel implements AdminViewModel {
  public userSearchQuery: string;
  public isUserSearching: boolean;
  public foundedUser: User | undefined;
  public userSearchError: string;
  public isUserSearchQueryError: boolean;

  public newUserNameQuery: string;
  public isNewUserNameError: boolean;
  public newUserEmailQuery: string;
  public isNewUserEmailQueryError: boolean;
  public newUserPasswordQuery: string;
  public isNewUserPasswordError: boolean;
  public isUserCreating: boolean;
  public userCreationError: string;

  public isUserDeleting: boolean;
  public userDeletionError: string;

  public isShowUserHasBeenAdded: boolean;
  public isShowUserHasBeenDeleted: boolean;

  private adminRepository: AdminRepository;

  public constructor(adminRepository: AdminRepository) {
    super();

    this.userSearchQuery = "";
    this.isUserSearching = false;
    this.foundedUser = undefined;
    this.userSearchError = "";
    this.isUserSearchQueryError = false;

    this.newUserNameQuery = "";
    this.isNewUserNameError = false;
    this.newUserEmailQuery = "";
    this.isNewUserEmailQueryError = false;
    this.newUserPasswordQuery = "";
    this.isNewUserPasswordError = false;
    this.isUserCreating = false;
    this.userCreationError = "";

    this.isUserDeleting = false;
    this.userDeletionError = "";

    this.adminRepository = adminRepository;

    this.isShowUserHasBeenAdded = false;
    this.isShowUserHasBeenDeleted = false;
  }

  public onCloseUserHasBeenAdded = (): void => {
    this.isShowUserHasBeenAdded = false;
    super.notifyViewAboutChanges();
  };

  public onCloseUserHasBeenDeleted = (): void => {
    this.isShowUserHasBeenDeleted = false;
    super.notifyViewAboutChanges();
  };

  public onNewUserNameQueryChanged = (query: string): void => {
    this.newUserNameQuery = query;
    this.isNewUserNameError = false;
    super.notifyViewAboutChanges();
  };

  public onNewUserEmailChanged = (query: string): void => {
    this.newUserEmailQuery = query;
    this.isNewUserEmailQueryError = false;
    super.notifyViewAboutChanges();
  };

  public onNewUserPasswordChanged = (query: string): void => {
    this.newUserPasswordQuery = query;
    this.isNewUserPasswordError = false;
    super.notifyViewAboutChanges();
  };

  public onGenerateNewPassword = (): void => {
    this.newUserPasswordQuery = uuidv4().substring(0, 8);
    this.isNewUserPasswordError = false;
    super.notifyViewAboutChanges();
  };

  public onUserSearchQueryChanged = (query: string): void => {
    this.userSearchQuery = query;
    this.userSearchError = "";
    this.isUserSearchQueryError = false;
    this.userDeletionError = "";
    this.foundedUser = undefined;
    super.notifyViewAboutChanges();
  };

  public onCreateUser = async (): Promise<void> => {
    this.isUserCreating = true;
    this.userCreationError = "";
    super.notifyViewAboutChanges();

    try {
      if (this.validateLoginFields()) {
        const newUserEmail = this.newUserEmailQuery;

        await this.adminRepository.onAddUser(this.newUserNameQuery, this.newUserEmailQuery, this.newUserPasswordQuery);
        this.newUserNameQuery = "";
        this.newUserEmailQuery = "";
        this.newUserPasswordQuery = "";

        this.isUserCreating = false;
        this.isShowUserHasBeenAdded = true;
        super.notifyViewAboutChanges();

        this.userSearchQuery = newUserEmail;
        this.onSearchUser();
      }
    } catch (e) {
      if (e instanceof Error) {
        this.userCreationError = e.message;
      } else {
        this.userCreationError = String(e); // Handle other types of errors
      }
    }

    this.isUserCreating = false;
    super.notifyViewAboutChanges();
  };

  public onSearchUser = async (): Promise<void> => {
    if (!this.userSearchQuery || !FormValidator.isValidEmail(this.userSearchQuery)) {
      this.isUserSearchQueryError = true;
      this.userDeletionError = "";
      super.notifyViewAboutChanges();
      return;
    }

    this.isUserSearching = true;
    this.isUserSearchQueryError = false;
    this.userSearchError = "";
    super.notifyViewAboutChanges();

    try {
      this.foundedUser = await this.adminRepository.onSearchForUser(this.userSearchQuery);
    } catch (e) {
      if (e instanceof Error) {
        this.userSearchError = e.message;
      } else {
        this.userSearchError = String(e); // Handle other types of errors
      }
    }

    this.isUserSearching = false;
    super.notifyViewAboutChanges();
  };

  public onDeleteUser = async (userEmail: string): Promise<void> => {
    this.isUserDeleting = true;
    this.userDeletionError = "";
    super.notifyViewAboutChanges();

    try {
      await this.adminRepository.onDeleteUser(userEmail);
      this.foundedUser = undefined;
      this.isShowUserHasBeenDeleted = true;
    } catch (e) {
      if (e instanceof Error) {
        this.userDeletionError = e.message;
      } else {
        this.userDeletionError = String(e); // Handle other types of errors
      }
    }

    this.isUserDeleting = false;
    super.notifyViewAboutChanges();
  };

  private validateLoginFields = (): boolean => {
    if (!this.newUserNameQuery) {
      this.isNewUserNameError = true;
      return false;
    }
    this.isNewUserNameError = false;

    if (!this.newUserEmailQuery || !FormValidator.isValidEmail(this.newUserEmailQuery)) {
      this.isNewUserEmailQueryError = true;
      return false;
    }
    this.isNewUserEmailQueryError = false;

    if (!this.newUserPasswordQuery) {
      this.isNewUserPasswordError = true;
      return false;
    }
    this.isNewUserPasswordError = false;

    return true;
  };
}
